<!--侧边栏组件-->
<template>
  <div
    class="sidebar-container"
    :class="{ 'is-collapse': isCollapse, 'mobile-sidebar': isMobile }"
    :style="containerStyle"
  >
    <!-- Logo区域 -->
    <div class="sidebar-logo" v-if="showLogo">
      <router-link to="/">
        <img v-if="logo" :src="logo" class="sidebar-logo-img" />
        <h1 v-else class="sidebar-logo-title">{{ title }}</h1>
      </router-link>
    </div>
    <!-- 搜索组件 -->
    <SidebarSearch v-if="showSearch && !isCollapse" />
    <!-- 滚动条 -->
    <div class="sidebar-menu">
      <el-scrollbar
        ref="scrollbarRef"
        wrap-class="scrollbar-wrapper"
        view-class="scrollbar-view"
        :class="{ 'is-collapse': isCollapse }"
      >
        <!-- 菜单组件 -->
        <SidebarMenu v-if="menuList.length > 0"
                     ref="menuRef"
                     :style="menuStyle"
                     :opened-menus="openedMenus"
        />
        <!-- 空状态 -->
        <SidebarEmpty
          v-else
          :empty-text="emptyText"
          :isEmpty="isEmpty"
          :loading="loading"
          :opened-menus="openedMenus"
        />
      </el-scrollbar>
    </div>
    <!-- 底部折叠按钮（手机端隐藏） -->
    <div class="sidebar-footer" v-if="showCollapse && !isMobile">
      <div class="collapse-button" @click="toggleCollapse">
        <el-icon>
          <Fold v-if="!isCollapse" />
          <Expand v-else />
        </el-icon>
        <span class="collapse-text" v-if="!isCollapse">收起菜单</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Fold, Expand } from '@element-plus/icons-vue'
import { useMenuStore } from '@/stores/modules/menu'
import { sidebarConfig } from '@/config/sidebar.config'
import SidebarSearch from './components/SidebarSearch.vue'
import SidebarMenu from './components/SidebarMenu.vue'
import SidebarEmpty from './components/SidebarEmpty.vue'

const emit = defineEmits(['close-mobile-menu'])
const menuStore = useMenuStore()
const scrollbarRef = ref()
const menuRef = ref()

// 手机端检测
const isMobile = ref(false)

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

// 关闭手机菜单
const closeMobileMenu = () => {
  emit('close-mobile-menu')
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

// ============ Props ============
interface Props {
  showSearch?: boolean
  showLogo?: boolean
  showCollapse?: boolean
  logo?: string
  title?: string
  emptyText?: string
  isEmpty?: boolean
  loading?: boolean
}

withDefaults(defineProps<Props>(), {
  showSearch: false,
  showLogo: false,
  showCollapse: false,
  logo: '',
  title: '管理系统',
  emptyText: '暂无菜单',
  isEmpty: false,
  loading: false
})

// ============ 计算属性 ============
const activeMenu = computed(() => menuStore.activeMenu)
const isCollapse = computed(() => menuStore.collapsed)
const openedMenus = computed(() => menuStore.openedMenus)
const menuList = computed(() => menuStore.sidebarMenus)

// 基础宽度计算
const baseWidth = computed(() =>
  isCollapse.value ? sidebarConfig.collapsedWidth : sidebarConfig.width
)

// 容器样式 - 使用配置文件中的变量
const containerStyle = computed(() => ({
  width: `${baseWidth.value}px`,
  '--sidebar-width': `${baseWidth.value}px`,
  '--sidebar-bg-color': sidebarConfig.backgroundColor,
  '--sidebar-hover-bg': sidebarConfig.menuHoverBackground,
  '--sidebar-hover-color': sidebarConfig.hoverColor,
  '--sidebar-active-color': sidebarConfig.activeTextColor,
  '--sidebar-text-color': sidebarConfig.textColor
}))

// 菜单样式
const menuStyle = computed(() => {
  const scrollbarWidth = sidebarConfig.scrollbarWidth || 4
  return {
    width: `${baseWidth.value - scrollbarWidth}px`,
    maxWidth: `${baseWidth.value - scrollbarWidth}px`
  }
})

// ============ 方法 ============
const toggleCollapse = () => {
  menuStore.collapsed = !menuStore.collapsed
}
</script>

<style lang="scss">
// ========== 平滑动画优化 ==========
// 子菜单容器 - 使用更平滑的缓动函数
.el-menu--inline {
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  max-height: 1000px;
  .el-menu-item {
    transition: background-color 0.2s ease,
    color 0.2s ease,
    opacity 0.2s ease,
    transform 0.2s ease;
    transform-origin: top;
  }
}

// 收起状态
.el-sub-menu:not(.is-opened) .el-menu--inline {
  max-height: 0 !important;
  .el-menu-item {
    opacity: 0;
    transform: translateY(-5px);
    transition: opacity 0.12s ease,
    transform 0.2s ease !important;
  }
}

// 展开状态
.el-sub-menu.is-opened .el-menu--inline {
  max-height: 1000px !important;
  .el-menu-item {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 0.2s ease,
    transform 0.2s ease !important;
  }
  @for $i from 1 through 10 {
    .el-menu-item:nth-child(#{$i}) {
      transition-delay: #{$i * 0.015}s;
    }
  }
}
// 子菜单标题图标旋转动画
.el-sub-menu__title {
  .el-icon {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
}
// 菜单项过渡优化
.el-sub-menu__title,
.el-menu-item {
  transition: background-color 0.2s ease,
  color 0.2s ease,
  padding-left 0.2s ease !important;
  will-change: background-color, color;
  span,
  .menu-title {
    transition: opacity 0.2s ease, visibility 0.2s ease;
    display: inline-block;
  }
}

// 折叠时隐藏内联菜单
.el-menu--collapse .el-sub-menu > .el-menu {
  display: none !important;
}

// 折叠模式下的菜单文字处理
.el-menu--collapse {
  .el-sub-menu__title,
  .el-menu-item {
    span,
    .menu-title {
      opacity: 0;
      visibility: hidden;
      width: 0;
      transition: opacity 0.15s ease,
      visibility 0.15s ease,
      width 0.2s ease !important;
    }
  }
  .el-menu--popup {
    span,
    .menu-title {
      opacity: 1;
      visibility: visible;
      width: auto;
    }
  }
}

// 非折叠模式恢复文字显示
.el-menu:not(.el-menu--collapse) {
  .el-sub-menu__title,
  .el-menu-item {
    span,
    .menu-title {
      opacity: 1;
      visibility: visible;
      width: auto;
      transition: opacity 0.2s ease 0.05s,
      visibility 0.2s ease 0.05s,
      width 0.2s ease !important;
    }
  }
}

// ========== 弹出菜单样式 - 只在折叠时弹出使用紧凑间距 ==========
.el-menu--popup {
  background-color: #fefefe !important;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  animation: menuPopupFadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  .el-menu-item {
    color: #304156 !important;
    transition: background-color 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease,
    padding-left 0.2s ease !important;
    .el-icon svg {
      fill: #304156 !important;
      color: #304156 !important;
      transition: fill 0.2s ease, color 0.2s ease;
    }
    &:not(:first-child) {
      border-top: 1px solid #ebeef5;
    }
    &:hover {
      background-color: rgba(0, 141, 188, 0.6) !important;
      color: #fff !important;
      .el-icon svg {
        fill: #fff !important;
        color: #fff !important;
      }
    }
    &.is-active {
      color: #008dbc !important;
      .el-icon svg {
        fill: #008dbc !important;
        color: #008dbc !important;
      }
      .menu-title {
        color: #008dbc !important;
      }
    }
  }

  // 手机端折叠时弹出的子菜单间距优化
  @media (max-width: 768px) {
    padding: 4px 0 !important;
    min-width: 140px !important;
    max-width: 180px !important;
    .el-menu-item {
      height: 32px !important;
      line-height: 32px !important;
      font-size: 12px !important;
      padding: 0 12px !important;
      margin: 0 !important;
      .el-icon {
        font-size: 14px !important;
        margin-right: 6px !important;
      }
      &:hover {
        transform: translateX(0);
      }
    }
    .el-sub-menu__title {
      height: 32px !important;
      line-height: 32px !important;
      font-size: 12px !important;
      padding: 0 12px !important;
      margin: 0 !important;
      .el-icon {
        font-size: 14px !important;
        margin-right: 6px !important;
      }
    }
  }
  @media (max-width: 480px) {
    padding: 2px 0 !important;
    min-width: 120px !important;
    max-width: 160px !important;
    .el-menu-item {
      height: 28px !important;
      line-height: 28px !important;
      font-size: 11px !important;
      padding: 0 10px !important;
      .el-icon {
        font-size: 12px !important;
        margin-right: 5px !important;
      }
    }
    .el-sub-menu__title {
      height: 28px !important;
      line-height: 28px !important;
      font-size: 11px !important;
      padding: 0 10px !important;
      .el-icon {
        font-size: 12px !important;
        margin-right: 5px !important;
      }
    }
  }
}

@keyframes menuPopupFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-5px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

// 折叠时父菜单激活状态优化
.el-menu--collapse {
  .el-sub-menu {
    > .el-sub-menu__title{
      .el-icon svg {
        font-size: 16px;
      }
    }
  }
  .el-sub-menu.is-active {
    > .el-sub-menu__title {
      color: #008dbc !important;
      .el-icon svg {
        font-size: 22px;
        fill: #008dbc !important;
        color: #008dbc !important;
      }
    }
  }
  .el-menu-item.is-active {
    color: #008dbc !important;
    .el-icon svg {
      fill: #008dbc !important;
      color: #008dbc !important;
    }
  }
}

// 全局过渡优化
.el-menu,
.el-sub-menu__title,
.el-menu-item {
  will-change: auto;
}

// 嵌套菜单过渡延迟
.el-menu--inline .el-menu--inline {
  .el-menu-item {
    transition-delay: 0.02s;
  }
  &.is-opened .el-menu-item {
    @for $i from 1 through 10 {
      &:nth-child(#{$i}) {
        transition-delay: #{$i * 0.012 + 0.03}s;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.sidebar-container {
  display: flex;
  flex-direction: column;
  box-sizing: content-box;
  height: 100%;
  width: var(--sidebar-width);
  overflow: hidden;
  background-color: var(--sidebar-bg-color);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  // 折叠状态
  &.is-collapse {
    .sidebar-search,
    .collapse-text {
      opacity: 0;
      visibility: hidden;
      width: 0;
      margin-left: 0;
      transition: opacity 0.15s ease,
      visibility 0.15s ease,
      width 0.2s ease,
      margin-left 0.2s ease;
    }
    .sidebar-logo {
      padding: 0;
      .sidebar-logo-title {
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.15s ease, visibility 0.15s ease;
      }
      .sidebar-logo-img {
        transform: scale(0.8);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
    }
  }

  // 非折叠状态恢复显示
  &:not(.is-collapse) {
    .sidebar-search,
    .collapse-text {
      opacity: 1;
      visibility: visible;
      transition: opacity 0.2s ease 0.05s,
      visibility 0.2s ease 0.05s,
      width 0.2s ease,
      margin-left 0.2s ease;
    }
    .sidebar-logo-title {
      opacity: 1;
      visibility: visible;
      transition: opacity 0.2s ease 0.05s, visibility 0.2s ease 0.05s;
    }
    .sidebar-logo-img {
      transform: scale(1);
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  // 手机端样式 - 非折叠状态保持正常间距
  &.mobile-sidebar {
    background-color: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    width: 260px !important;

    .sidebar-logo,
    .sidebar-search,
    .sidebar-menu,
    .sidebar-footer,
    :deep(.el-menu),
    :deep(.el-sub-menu__title),
    :deep(.el-menu-item) {
      background-color: transparent !important;
    }

    .sidebar-logo-title,
    :deep(.el-sub-menu__title),
    :deep(.el-menu-item) {
      color: #333 !important;
      font-size: 14px !important;
    }

    .sidebar-logo {
      border-bottom-color: rgba(0, 0, 0, 0.1);
      height: 48px;
      line-height: 48px;

      .sidebar-logo-img {
        height: 28px;
      }
      .sidebar-logo-title {
        font-size: 14px;
      }
    }

    // 手机端非折叠状态 - 保持原有样式，不覆盖 padding
    .sidebar-menu {
      :deep(.el-menu) {
        width: 100% !important;

        // 不覆盖原有的 padding，让菜单项保持原有内边距
        .el-sub-menu__title,
        .el-menu-item {
          height: 44px !important;
          line-height: 44px !important;
          font-size: 14px !important;
          // 移除 padding: 0 的覆盖，让原有的 padding 生效

          .el-icon {
            font-size: 16px !important;
            margin-right: 8px !important;
          }
        }

        .el-menu--inline {
          .el-menu-item {
            padding-left: 44px !important;  // 保持子菜单缩进
            height: 40px !important;
            line-height: 40px !important;
            font-size: 13px !important;
          }
        }
      }
    }

    :deep(.el-sub-menu__title:hover),
    :deep(.el-menu-item:hover) {
      background-color: rgba(0, 0, 0, 0.05) !important;
      color: #008dbc !important;
    }

    :deep(.el-menu-item.is-active) {
      background-color: rgba(0, 141, 188, 0.1) !important;
      color: #008dbc !important;
    }

    :deep(.el-icon svg) {
      fill: #666 !important;
      color: #666 !important;
    }

    :deep(.el-sub-menu__title:hover .el-icon svg),
    :deep(.el-menu-item:hover .el-icon svg) {
      fill: #008dbc !important;
      color: #008dbc !important;
    }

    :deep(.el-menu-item.is-active .el-icon svg) {
      fill: #008dbc !important;
      color: #008dbc !important;
    }
  }

  // Logo区域
  .sidebar-logo {
    position: relative;
    height: 54px;
    line-height: 54px;
    text-align: center;
    overflow: hidden;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    flex-shrink: 0;

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      text-decoration: none;
    }
    .sidebar-logo-img {
      height: 32px;
      width: auto;
      max-width: 120px;
      object-fit: contain;
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .sidebar-logo-title {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #304156;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  // 搜索区域
  .sidebar-search {
    padding: 15px 12px 8px;
    background: var(--sidebar-bg-color);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    position: relative;
    flex-shrink: 0;
    overflow: hidden;

    :deep(.el-input) {
      .el-input__wrapper {
        background-color: rgba(0, 0, 0, 0.05);
        border: 1px solid transparent;
        border-radius: 4px;
        box-shadow: none;

        &:hover {
          background-color: rgba(0, 0, 0, 0.08);
        }
        &.is-focus {
          background-color: rgba(0, 141, 188, 0.1);
          border-color: var(--sidebar-active-color);
        }
      }
      .el-input__inner {
        color: var(--sidebar-text-color);
        &::placeholder {
          color: rgba(0, 0, 0, 0.4);
        }
      }
      .el-input__prefix {
        color: var(--sidebar-text-color);
      }
    }
  }

  // 菜单区域
  .sidebar-menu {
    flex: 1;
    min-height: 0;
    overflow: hidden;

    :deep(.el-scrollbar) {
      height: 100%;
    }

    :deep(.el-menu){
      border-right: none !important;

      .el-sub-menu__title,
      .el-menu-item {
        height: 44px;
        line-height: 44px;
      }

      .el-sub-menu__title:hover,
      .el-menu-item:hover {
        background: var(--sidebar-hover-bg) !important;
        color: var(--sidebar-hover-color) !important;

        .el-icon svg {
          fill: var(--sidebar-hover-color) !important;
          color: var(--sidebar-hover-color) !important;
          transition: fill 0.2s ease, color 0.2s ease;
        }
      }

      .el-sub-menu__title.is-active,
      .el-menu-item.is-active {
        color: var(--sidebar-active-color) !important;

        .el-icon svg {
          fill: var(--sidebar-active-color) !important;
          color: var(--sidebar-active-color) !important;
          transition: fill 0.2s ease, color 0.2s ease;
        }
      }
    }
  }

  // 底部区域
  .sidebar-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    flex-shrink: 0;

    .collapse-button {
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: var(--sidebar-text-color);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        background: var(--sidebar-hover-bg);
        color: var(--sidebar-hover-color);
      }

      .el-icon {
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        font-size: 18px;
      }

      .collapse-text {
        margin-left: 8px;
        font-size: 14px;
        white-space: nowrap;
        overflow: hidden;
      }
    }
  }

  // 滚动条样式
  :deep(.el-scrollbar) {
    height: 100%;

    .scrollbar-wrapper {
      overflow-x: hidden !important;
    }

    .el-scrollbar__bar {
      &.is-vertical {
        width: 4px;
      }
      .el-scrollbar__thumb {
        background-color: rgba(0, 0, 0, 0.2);
        &:hover {
          background-color: rgba(0, 0, 0, 0.3);
        }
      }
    }

    &.is-collapse {
      .el-scrollbar__thumb {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }
}

// ========== 手机端全局响应式调整 ==========
@media (max-width: 768px) {
  .sidebar-container {
    // 手机端菜单项高度调整 - 只调整基础高度，保持内边距
    .sidebar-logo {
      height: 48px;
      line-height: 48px;

      .sidebar-logo-img {
        height: 28px;
      }
      .sidebar-logo-title {
        font-size: 14px;
      }
    }

    .sidebar-search {
      padding: 10px 12px;
    }

    // 注意：这里不覆盖 padding，让菜单项保持原有的左右内边距
    .sidebar-menu {
      :deep(.el-menu) {
        .el-sub-menu__title,
        .el-menu-item {
          height: 40px;
          line-height: 40px;
          font-size: 13px;

          .el-icon svg {
            font-size: 16px;
          }
        }

        .el-menu--inline .el-menu-item {
          padding-left: 44px !important;
          height: 38px;
          line-height: 38px;
          font-size: 12px;
        }
      }
    }

    .sidebar-footer {
      .collapse-button {
        height: 44px;

        .el-icon {
          font-size: 16px;
        }

        .collapse-text {
          font-size: 12px;
        }
      }
    }
  }
}

// 小屏手机
@media (max-width: 480px) {
  .sidebar-container {
    .sidebar-menu {
      :deep(.el-menu) {
        .el-sub-menu__title,
        .el-menu-item {
          height: 38px;
          line-height: 38px;
          font-size: 12px;

          .el-icon svg {
            font-size: 15px;
          }
        }

        .el-menu--inline .el-menu-item {
          padding-left: 40px !important;
          height: 36px;
          line-height: 36px;
          font-size: 11px;
        }
      }
    }
  }
}
</style>
